// eslint-disable-next-line
import React, { useCallback, useEffect } from 'react';
import { hasAdminAccess } from '../../../../utils/user';
import { useStateValue, useStateDispatch } from '../../../../context/AppContext';
import { GET_STUDENT_PAGINATED_TEST_STATUS, GET_DIGITAL_STUDENTS_PAGINATED_ROSTER } from '../../../../apollo/queries';
import { fetchMoreStudents as generateFetchMoreOptions } from '../../../../utils/pagination';
import ReloadButton from '../../common/ReloadButton';
import { errorHandler } from '../../../common/network/QueryComponent';
import EstEndTimeNotification from './EstEndTimeNotification';
import { TestDayRoster } from './TestDayRoster';
import { useLazyQuery } from '@apollo/client/react';
import { TDTKQuery } from '../../../../apollo/types';

const POLL_INTERVAL_STUDENT_STATUS = 90000;
const DEFAULT_TEST_STATUS_VARS = { input: { limit: 5000 } };

export default function Container() {
  const { user } = useStateValue();
  const dispatch = useStateDispatch();
  const isAdmin = hasAdminAccess(user.rid, user.role);

  const queryStudentsPaginatedRoster = {
    kind: 'GetDigitalStudentsPaginatedRoster',
    specification: GET_DIGITAL_STUDENTS_PAGINATED_ROSTER,
    // eslint-disable-next-line
  } as TDTKQuery<any>;

  const [getPaginatedRoster, { data: studentData, error: errorStudents, fetchMore: fetchMoreStudents }] = useLazyQuery(
    GET_DIGITAL_STUDENTS_PAGINATED_ROSTER,
    {
      fetchPolicy: 'network-only',
    }
  );

  // eslint-disable-next-line
  const queryStudentPaginatedTestStatus: TDTKQuery<any> = {
    kind: 'GetStudentPaginatedTestStatus',
    specification: GET_STUDENT_PAGINATED_TEST_STATUS,
    variables: DEFAULT_TEST_STATUS_VARS,
  };

  const [
    getStudentPaginatedTestStatus,
    {
      data: studentTestStatusesData,
      error: errorStudentTestStatuses,
      fetchMore: fetchMoreStudentTestStatuses,
      refetch: refetchStudentTestStatuses,
    },
  ] = useLazyQuery(GET_STUDENT_PAGINATED_TEST_STATUS, {
    pollInterval: POLL_INTERVAL_STUDENT_STATUS,
    fetchPolicy: 'network-only',
  });

  const fetchMoreStudentsCallBack = useCallback(async () => {
    const options = generateFetchMoreOptions(studentData);

    if (options) {
      await fetchMoreStudents(options);
    }
  }, [studentData, fetchMoreStudents]);

  useEffect(() => {
    fetchMoreStudentsCallBack();
    // eslint-disable-next-line
  }, [studentData?.paginatedStudents?.lastKey]);

  const fetchMoreStudentStatusesCallBack = useCallback(async () => {
    const options = generateFetchMoreOptions(studentTestStatusesData);

    if (options) {
      await fetchMoreStudentTestStatuses(options);
    }
  }, [studentTestStatusesData, fetchMoreStudentTestStatuses]);

  useEffect(() => {
    fetchMoreStudentStatusesCallBack();
    // eslint-disable-next-line
  }, [studentTestStatusesData]);

  useEffect(() => {
    getStudentPaginatedTestStatus({ variables: DEFAULT_TEST_STATUS_VARS });
    getPaginatedRoster({ variables: { input: {} } });
    // eslint-disable-next-line
  }, []);

  const allStudents = (studentData?.paginatedStudents?.students ?? []).filter(Boolean);
  const lastKeyStudents = studentData?.paginatedStudents?.lastKey;
  const lastKeyStudentTestStatuses = studentTestStatusesData?.paginatedStudentTestStatus?.lastKey;

  const studentsTestStatuses = (studentTestStatusesData?.paginatedStudentTestStatus?.students ?? []).filter(Boolean);

  if (errorStudentTestStatuses) {
    errorHandler(
      dispatch,
      queryStudentPaginatedTestStatus,
      true,
      studentTestStatusesData,
      studentTestStatusesData,
      errorStudentTestStatuses
    );
  }
  if (errorStudents) {
    errorHandler(dispatch, queryStudentsPaginatedRoster, true, studentData, studentData, errorStudents);
  }
  return isAdmin ? (
    <div className='container-fluid'>
      <div className='row px-0 mx-0'>
        <div className='col-md-11 px-0'>
          <h2 className='tdtk-h2 py-4' id='test-admin-tools'>
            Test Day Live
          </h2>
          <p className='pb-4 pb-sm-3' id='students-live-data' aria-live='polite' aria-label='Test Day Live information'>
            Watch as students in all rooms progress through the test. Click a filter to display only students with each
            status.
          </p>
        </div>
        <div className='col-12 col-md-1 px-0 m-3 m-md-0 d-flex align-items-center'>
          <ReloadButton
            text={'Refresh'}
            refetch={refetchStudentTestStatuses}
            reloadButtonID='StdRosterReloadData'
            buttonStyle='secondary'
          />
        </div>
      </div>
      <div className='row px-0 mx-0 my-3'>
        <EstEndTimeNotification />
      </div>
      <TestDayRoster
        allStudents={allStudents}
        studentsTestStatuses={studentsTestStatuses}
        lastKeyStudents={lastKeyStudents}
        lastKeyStudentTestStatuses={lastKeyStudentTestStatuses}
      />
    </div>
  ) : (
    <></>
  );
}
